/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Grid, GridItem, Heading, Text } from '@chakra-ui/react';
import { LoaderOverlay } from 'components/LoaderOverlay/LoaderOverlay';
import { useCartContext } from 'context/CartContext/CartContext';
import { GameInfo } from 'pages/EventPage/GameInfo/GameInfo';
import { TicketCart } from 'pages/EventPage/TicketCart/TicketCart';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ROUTES } from 'router/routes';
import { Stadium } from './Stadium/Stadium';
import { TimeslotSelector } from './TimeslotSelector/TimeslotSelector';
import { useEventQuery } from './hooks/useEvent';
import { useIsAuthenticated } from 'react-auth-kit';
import { ModalLogin } from 'components/ModalLogin/ModalLogin';
import { LoginInformation } from './LoginInformation/LoginInformation';
import { GoodToKnowCard } from 'components/GoodToKnow/GoodToKnowCard';
import { FormattedMessage } from 'react-intl';
import { useEventTracking } from 'hooks/useEventTracking';
import { getInvitation } from 'api/invitations';
import { useTimeslotSelection } from 'hooks/useTimeslotSelection';
import axios from 'axios';

export const EventPage = () => {
  const { eventTracking } = useEventTracking();
  const navigate = useNavigate();
  useEffect(() => {
    eventTracking.page.viewed({
      pageName: 'event_page',
      url: window.location.href,
    });
  }, [eventTracking]);
  const { eventKey } = useParams();
  const [searchParams] = useSearchParams();
  const invitationId = searchParams.get('invitationId') ?? undefined;
  const isAuthenticated = useIsAuthenticated();
  const authenticated = isAuthenticated();

  const queryEnabled = !invitationId || authenticated;
  const { data, isFetching, isError } = useEventQuery(eventKey, invitationId, queryEnabled);
  const { tickets, setEventId, setEventKey, setInvitationId, clearCart } = useCartContext();

  // Clear stale cart on every page mount (handles refresh + back-from-checkout).
  useEffect(() => {
    clearCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isValidSelection, setIsValidSelection] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [invitationValidated, setInvitationValidated] = useState(false);
  const [pendingCloseCheck, setPendingCloseCheck] = useState(false);
  // Keyed on the event so the slot selection resets if the event changes (even
  // without a remount), preventing one event's slot from leaking to another.
  const { selectedSlotEventKey, selectedSlotPricing, handleSlotSelect } = useTimeslotSelection(
    data?.eventKey,
  );

  const hasTimeslots = Boolean(data?.timeslots && data.timeslots.length > 0);
  const effectiveEventKey =
    hasTimeslots && selectedSlotEventKey ? selectedSlotEventKey : data?.eventKey ?? '';
  const effectivePricing =
    hasTimeslots && selectedSlotPricing ? selectedSlotPricing : data?.pricing ?? [];

  useEffect(() => {
    if (data && !isFetching) {
      if (!data.timeslots || data.timeslots.length === 0) {
        setEventId(data.eventId);
        setEventKey(data.eventKey);
      }
      if (invitationId) setInvitationId(invitationId);

      eventTracking.event.viewed({
        eventId: data.eventId,
        eventName:
          data.teamA?.name && data.teamB?.name
            ? `${data.teamA.name} vs ${data.teamB.name}`
            : data.eventKey,
        eventDate: data.date,
        venue: data.stadium || '',
      });
    }
  }, [data?.eventKey, data?.id]);

  useEffect(() => {
    setInvitationValidated(false);
    setInvitationError(null);
  }, [invitationId]);

  useEffect(() => {
    if (!invitationId || !authenticated) return;
    setInvitationId(invitationId);
    getInvitation(invitationId)
      .then(() => setInvitationValidated(true))
      .catch((e) => {
        const status = axios.isAxiosError(e) ? e.response?.status : undefined;
        setInvitationError(
          status === 403 ? 'هذا الرقم غير مدعو لهذا الحدث' : 'حدث خطأ في التحقق من الدعوة',
        );
      });
  }, [invitationId, authenticated]);

  useEffect(() => {
    if (!pendingCloseCheck) return;
    setPendingCloseCheck(false);
    if (!authenticated) navigate(ROUTES.home);
  }, [pendingCloseCheck, authenticated, navigate]);

  const handleOpenModal = () => {
    if (!isAuthenticated()) {
      eventTracking.login.clicked({});
      setIsAuthModalOpen(true);
    }
  };

  if (!queryEnabled) {
    return <ModalLogin isOpen onClose={() => setPendingCloseCheck(true)} />;
  }

  if (isFetching) {
    return <LoaderOverlay />;
  }

  if (!data || isError) return null;

  const isInvitationOnly = data.isInvitationBased;
  const needsInvitationValidation =
    isInvitationOnly &&
    invitationId &&
    isAuthenticated() &&
    !invitationValidated &&
    !invitationError;

  const renderSeatingArea = () => {
    if (isInvitationOnly && !invitationId) {
      return (
        <Box p={8} textAlign='center'>
          <Text textStyle='headlineMedium' color='dark.D900'>
            <FormattedMessage
              id='invitation.error.inviteOnly'
              defaultMessage='هذا الحدث بالدعوة فقط'
            />
          </Text>
        </Box>
      );
    }
    if (invitationError) {
      return (
        <Box p={8} textAlign='center'>
          <Text textStyle='headlineMedium' color='red'>
            {invitationError}
          </Text>
        </Box>
      );
    }
    if (needsInvitationValidation) {
      return <LoaderOverlay />;
    }
    return (
      <Stadium
        key={selectedSlotEventKey ?? data.eventKey}
        pricing={effectivePricing}
        event={effectiveEventKey}
        eventId={data.eventId}
        currency={data.currency}
        onOpenModal={handleOpenModal}
        maxAllowedTickets={data.maxAllowedTickets}
        setIsValidSelection={setIsValidSelection}
        disabled={hasTimeslots && !selectedSlotEventKey}
      />
    );
  };

  return (
    <Box dir='rtl'>
      <GameInfo {...data} hasTimeslots={hasTimeslots} />
      {data.timeslots && data.timeslots.length > 0 && (
        <TimeslotSelector
          timeslots={data.timeslots}
          selectedEventKey={selectedSlotEventKey}
          onSelect={handleSlotSelect}
        />
      )}
      <Grid mx='auto' maxW='1440px' templateColumns={{ base: '1fr', md: '460px 1fr' }}>
        <GridItem h={{ md: '1000px' }} minH='230px'>
          {!isAuthenticated() ? (
            <LoginInformation onModalOpen={handleOpenModal} />
          ) : (
            <TicketCart
              currency={data.currency}
              selectedTickets={tickets}
              isValidSelection={isValidSelection}
              eventId={data.eventId}
              isFreeEvent={data.isFreeEvent}
            />
          )}
        </GridItem>
        <GridItem rowStart={{ base: 1, md: 'auto' }} alignSelf='start' h={{ md: '1000px' }}>
          {renderSeatingArea()}
        </GridItem>
      </Grid>
      {data.goodToKnow && data.goodToKnow.length > 0 && (
        <Box>
          <Heading
            my={12}
            textAlign='center'
            textStyle='headingSmall'
            color='dark.D900'
            fontWeight='600'
          >
            <FormattedMessage id='eventpage.goodToKnow' defaultMessage='معلومات مهمة' />
          </Heading>
          <GoodToKnowCard items={data.goodToKnow} />
        </Box>
      )}

      <ModalLogin isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </Box>
  );
};
